import { useResponseItemContext } from '@app/src/context/ResponseItemContext'
import { useProviderReport } from '@app/src/pages/Questionnaire/Provider/ProviderReportContext'
import { getPreviousAnswerSource } from '@app/src/pages/Questionnaire/Provider/Report/SuggestedAnswers/suggestionSources'
import { useReport } from '@app/src/pages/Questionnaire/ReportContext'
import { ReuseAllQuestionsStates } from '@app/src/pages/Questionnaire/ReportContextProvider'
import type { CoordinateLocation } from '@app/src/types/resourceExplorer'
import { Box, Button, Stack } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import CoordinateInput from './CoordinateInput'

// Generate a unique ID for React keys
const generateId = () => Math.random().toString(36).substring(2) + Date.now().toString(36)

interface CoordinateListInputProps {
  requestItemId: number
  allowMultiple: boolean
  disabled?: boolean
}

const MAX_COORDINATES = 50

const emptyCoordinate: CoordinateLocation = {
  latitude: undefined,
  longitude: undefined,
  description: '',
  order: 0,
}

const CoordinateListInput: React.FC<CoordinateListInputProps> = ({ requestItemId, allowMultiple, disabled }) => {
  const { formatMessage } = useIntl()
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext()
  const { reuseAllQuestionsState } = useReport()
  const { previousAnswer, setAppliedSuggestion } = useResponseItemContext()
  const { recordAutofill } = useProviderReport()

  const fieldName = `${requestItemId}.coordinates`
  const watchedCoordinates = watch(fieldName) as CoordinateLocation[] | undefined

  const hasError = Boolean(errors?.[`${requestItemId}`])
  const error = hasError ? errors[`${requestItemId}`]?.message : null

  // Ensure each coordinate has a stable clientId for React keys
  const coordinatesWithKeys: CoordinateLocation[] = useMemo(() => {
    const coords =
      Array.isArray(watchedCoordinates) && watchedCoordinates.length > 0 ? watchedCoordinates : [{ ...emptyCoordinate }]

    return coords.map(coord => {
      if (coord.clientId) return coord
      const clientId = coord.id?.toString() || generateId()
      return {
        ...coord,
        clientId,
      }
    })
  }, [watchedCoordinates])

  const coordinates = coordinatesWithKeys

  useEffect(() => {
    if (reuseAllQuestionsState === ReuseAllQuestionsStates.ApplyAll && previousAnswer?.coordinates?.length) {
      setAppliedSuggestion({ source: getPreviousAnswerSource(previousAnswer) })
      setValue(fieldName, previousAnswer.coordinates)
      recordAutofill(getPreviousAnswerSource(previousAnswer))
    }
  }, [reuseAllQuestionsState])

  const updateCoordinates = (updated: CoordinateLocation[]) => {
    setValue(
      fieldName,
      updated.map((c, i) => ({ ...c, order: i })),
    )
  }

  const handleChange = (index: number, value: CoordinateLocation) => {
    const updated = [...coordinates]
    updated[index] = value
    updateCoordinates(updated)
  }

  const handleAdd = () => {
    if (coordinates.length >= MAX_COORDINATES) return
    updateCoordinates([...coordinates, { ...emptyCoordinate, clientId: generateId(), order: coordinates.length }])
  }

  const handleRemove = (index: number) => {
    updateCoordinates(coordinates.filter((_, i) => i !== index))
  }

  if (!allowMultiple) {
    return (
      <CoordinateInput
        value={coordinates[0] || emptyCoordinate}
        onChange={val => updateCoordinates([val])}
        disabled={disabled}
        hasError={hasError}
        error={error as string}
      />
    )
  }

  return (
    <Stack spacing={3} sx={{ p: 2.5 }}>
      {coordinates.map((coord, index) => (
        <Box
          key={coord.clientId || coord.id || index}
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: 'background.paper',
          }}
        >
          <CoordinateInput
            value={coord}
            onChange={val => handleChange(index, val)}
            onRemove={coordinates.length > 1 ? () => handleRemove(index) : undefined}
            disabled={disabled}
            index={index}
            hasError={hasError}
            error={error as string}
          />
        </Box>
      ))}
      {coordinates.length < MAX_COORDINATES && (
        <Button variant="outlined" onClick={handleAdd} disabled={disabled} sx={{ alignSelf: 'flex-start' }}>
          {formatMessage({ id: 'general.addLocation' })}
        </Button>
      )}
    </Stack>
  )
}

export default CoordinateListInput
